import { useState, useEffect } from 'react';
import axios from 'axios';
import { Car, Calendar, Clock, Phone, Mail, User, Check, X, Trash2, Search, RefreshCw } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  confirmed: 'bg-blue-100 text-blue-700 border-blue-200',
  completed: 'bg-green-100 text-green-700 border-green-200',
  cancelled: 'bg-red-100 text-red-700 border-red-200',
};

const statusLabels = {
  pending: 'En attente',
  confirmed: 'Confirmé',
  completed: 'Terminé',
  cancelled: 'Annulé',
};

const AdminTestDrives = () => {
  const { getAuthHeader } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/test-drives`, {
        headers: getAuthHeader(),
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Erreur lors du chargement des réservations');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (bookingId, newStatus) => {
    try {
      await axios.put(`${API}/test-drives/${bookingId}/status?status=${newStatus}`, {}, {
        headers: getAuthHeader(),
      });
      toast.success(`Statut mis à jour: ${statusLabels[newStatus]}`);
      fetchBookings();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette réservation?')) return;
    
    try {
      await axios.delete(`${API}/test-drives/${bookingId}`, {
        headers: getAuthHeader(),
      });
      toast.success('Réservation supprimée');
      fetchBookings();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    return new Date(dateStr).toLocaleString('fr-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredBookings = bookings.filter(b => {
    const matchesSearch = 
      b.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.vehicle_info?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.phone?.includes(searchTerm);
    
    const matchesStatus = statusFilter === 'all' || b.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const pendingCount = bookings.filter(b => b.status === 'pending').length;
  const confirmedCount = bookings.filter(b => b.status === 'confirmed').length;
  const todayCount = bookings.filter(b => {
    const today = new Date().toISOString().split('T')[0];
    return b.preferred_date === today && (b.status === 'pending' || b.status === 'confirmed');
  }).length;

  return (
    <AdminLayout>
      <div className="space-y-4" data-testid="admin-test-drives">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Car className="h-5 w-5 text-green-600" />
              Essais Routiers
            </h1>
            <p className="text-slate-500 text-xs">Gérer les demandes d'essais routiers</p>
          </div>
          <Button onClick={fetchBookings} variant="outline" size="sm" className="text-xs">
            <RefreshCw className="h-3 w-3 mr-1" />
            Actualiser
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-yellow-600" />
              <div>
                <p className="text-xl font-bold text-yellow-700">{pendingCount}</p>
                <p className="text-[10px] text-slate-500">En attente</p>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-blue-600" />
              <div>
                <p className="text-xl font-bold text-blue-700">{confirmedCount}</p>
                <p className="text-[10px] text-slate-500">Confirmés</p>
              </div>
            </div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-green-600" />
              <div>
                <p className="text-xl font-bold text-green-700">{todayCount}</p>
                <p className="text-[10px] text-slate-500">Aujourd'hui</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher..."
              className="pl-8 text-xs h-8"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-1">
            {['all', 'pending', 'confirmed'].map((status) => (
              <Button
                key={status}
                variant={statusFilter === status ? 'default' : 'outline'}
                onClick={() => setStatusFilter(status)}
                size="sm"
                className="text-[10px] h-8 px-2"
              >
                {status === 'all' ? 'Tous' : status === 'pending' ? 'En attente' : 'Confirmés'}
              </Button>
            ))}
          </div>
        </div>

        {/* Bookings List */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <Car className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p className="text-xs">Aucune réservation trouvée</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredBookings.map((booking) => (
              <div
                key={booking.id}
                className="bg-white border border-slate-200 rounded-lg p-3 hover:border-slate-300 transition-colors"
                data-testid={`booking-${booking.id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="h-3 w-3 text-slate-400" />
                      <span className="font-medium text-slate-800 text-sm">{booking.name}</span>
                      <Badge className={`text-[10px] ${statusColors[booking.status]}`}>
                        {statusLabels[booking.status]}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-3 text-[11px] text-slate-500">
                      <a href={`mailto:${booking.email}`} className="flex items-center gap-1 hover:text-blue-600">
                        <Mail className="h-3 w-3" />
                        {booking.email}
                      </a>
                      <a href={`tel:${booking.phone}`} className="flex items-center gap-1 hover:text-blue-600">
                        <Phone className="h-3 w-3" />
                        {booking.phone}
                      </a>
                    </div>
                    <div className="bg-slate-50 rounded p-2">
                      <div className="flex items-center gap-2 text-blue-600 font-medium text-xs">
                        <Car className="h-3 w-3" />
                        {booking.vehicle_info || 'Véhicule non spécifié'}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="flex items-center gap-1 text-green-600">
                        <Calendar className="h-3 w-3" />
                        {formatDate(booking.preferred_date)}
                      </span>
                      <span className="flex items-center gap-1 text-blue-600">
                        <Clock className="h-3 w-3" />
                        {booking.preferred_time}
                      </span>
                    </div>
                    {booking.message && (
                      <p className="text-[11px] text-slate-500 italic">"{booking.message}"</p>
                    )}
                    <p className="text-[10px] text-slate-400">
                      Reçu: {formatDateTime(booking.created_at)}
                    </p>
                  </div>

                  <div className="flex flex-row lg:flex-col gap-1">
                    {booking.status === 'pending' && (
                      <Button size="sm" onClick={() => updateStatus(booking.id, 'confirmed')} className="bg-blue-600 hover:bg-blue-700 text-[10px] h-7">
                        <Check className="h-3 w-3 mr-1" />
                        Confirmer
                      </Button>
                    )}
                    {booking.status === 'confirmed' && (
                      <Button size="sm" onClick={() => updateStatus(booking.id, 'completed')} className="bg-green-600 hover:bg-green-700 text-[10px] h-7">
                        <Check className="h-3 w-3 mr-1" />
                        Terminé
                      </Button>
                    )}
                    {(booking.status === 'pending' || booking.status === 'confirmed') && (
                      <Button size="sm" variant="outline" onClick={() => updateStatus(booking.id, 'cancelled')} className="text-red-600 border-red-200 hover:bg-red-50 text-[10px] h-7">
                        <X className="h-3 w-3 mr-1" />
                        Annuler
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => deleteBooking(booking.id)} className="text-slate-400 hover:text-red-600 text-[10px] h-7">
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminTestDrives;
